<script lang="ts">
  interface UsageInfo {
    type: 'free' | 'trial' | 'premium';
    dailySpellCheckLimit: number;
    currentDailyUsage: {
      spellChecks: number;
      aiAnalyses: number;
    };
    monthlyUsage: number;
  }
  
  interface Props {
    usage: UsageInfo;
  }
  
  let { usage }: Props = $props();
  
  // Utility functions
  function getMonthlyLimit(dailyLimit: number): number | null {
    if (dailyLimit === -1) {
      return null; // Unlimited
    }
    return dailyLimit * 30;
  }
  
  function getUsagePercentage(current: number, limit: number): number {
    if (limit === -1) {
      return 0; // Unlimited
    }
    return Math.min((current / limit) * 100, 100);
  }
  
  let monthlyLimit = $derived(getMonthlyLimit(usage.dailySpellCheckLimit));
  let dailyPercentage = $derived(getUsagePercentage(
    usage.currentDailyUsage.spellChecks, 
    usage.dailySpellCheckLimit
  ));
  let monthlyPercentage = $derived(monthlyLimit 
    ? getUsagePercentage(usage.monthlyUsage, monthlyLimit)
    : 0);
</script>

<div class="server-service-section">
  <div class="server-header">
    <div class="server-title">
      <h3>맞춤법 서버</h3>
      <span class="server-badge {usage.type}">
        {#if usage.type === 'premium'}
          프리미엄
        {:else if usage.type === 'trial'}
          체험판
        {:else}
          무료
        {/if}
      </span>
    </div>
    <span class="provider-text">Bareun.ai</span>
  </div>
  
  <!-- Usage Information -->
  <div class="usage-section">
    <div class="usage-row">
      <span class="usage-label">일일 사용량</span>
      <div class="usage-info">
        <div class="usage-bar">
          <div 
            class="usage-fill" 
            style="width: {dailyPercentage}%"
          ></div>
        </div>
        <span class="usage-text">
          {usage.currentDailyUsage.spellChecks} / {usage.dailySpellCheckLimit === -1 ? '무제한' : usage.dailySpellCheckLimit}
        </span>
      </div>
    </div>
    
    <div class="usage-row">
      <span class="usage-label">월간 사용량</span>
      <div class="usage-info">
        <div class="usage-bar">
          <div 
            class="usage-fill" 
            style="width: {monthlyPercentage}%"
          ></div>
        </div>
        <span class="usage-text">
          {usage.monthlyUsage.toLocaleString()} / {monthlyLimit === null ? '무제한' : monthlyLimit.toLocaleString()}
        </span>
      </div>
    </div>
  </div>
  
  <div class="recommendation-box">
    <span class="recommendation-icon">💡</span>
    <p class="recommendation-text">
      AI 분석 이전에 맞춤법 검사를 먼저 수행하는 것을 추천합니다.
    </p>
  </div>
  
  {#if usage.type === 'free'}
    <div class="notice-box">
      <span class="notice-icon">💡</span>
      <div class="notice-content">
        <p class="notice-title">무료 사용자 팁</p>
        <p class="notice-text">
          {#if usage.currentDailyUsage.spellChecks >= usage.dailySpellCheckLimit}
            무료 사용 한도가 초과되었습니다. Bareun.ai API를 직접 연결하면 무제한 검사가 가능합니다.
          {:else}
            Bareun.ai API를 직접 연결하면 무제한 검사가 가능합니다.
          {/if}
        </p>
      </div>
    </div>
  {/if}
</div>

<style>
  /* Server Service Section */
  .server-service-section {
    padding: 20px 0;
    border-bottom: 1px solid var(--border-primary);
    margin-bottom: 20px;
  }
  
  .server-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  
  .server-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .server-title h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .server-badge {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 1px solid var(--border-primary);
  }
  
  .server-badge.premium {
    background: linear-gradient(135deg, #7c3aed, #3b82f6);
    border: none;
    color: white;
  }
  
  .server-badge.trial {
    background: linear-gradient(135deg, #f59e0b, #f97316);
    border: none;
    color: white;
  }
  
  .provider-text {
    font-size: 10px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  /* Usage Section */
  .usage-section {
    padding: 12px 0;
    margin-bottom: 12px;
  }
  
  .usage-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
  }
  
  .usage-row:not(:last-child) {
    border-bottom: 1px solid var(--border-primary);
  }
  
  .usage-label {
    font-size: 11px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  
  .usage-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    max-width: 200px;
  }
  
  .usage-bar {
    flex: 1;
    height: 2px;
    background: var(--bg-tertiary);
    overflow: hidden;
  }
  
  .usage-fill {
    height: 100%;
    background: var(--success);
    transition: width 0.3s ease;
  }
  
  .usage-text {
    font-size: 10px;
    color: var(--text-primary);
    font-weight: 500;
    white-space: nowrap;
  }
  
  /* Recommendation Box */
  .recommendation-box {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    background: var(--bg-tertiary);
    border-radius: 6px;
  }
  
  .recommendation-icon {
    font-size: 12px;
    opacity: 0.7;
  }
  
  .recommendation-text {
    margin: 0;
    font-size: 11px;
    color: var(--text-secondary);
  }
  
  /* Notice Box */
  .notice-box {
    display: flex;
    gap: 8px;
    padding: 8px 10px;
    background: rgba(245, 158, 11, 0.05);
    border: 1px solid rgba(245, 158, 11, 0.15);
    border-radius: 6px;
    margin-top: 10px;
  }
  
  .notice-icon {
    font-size: 12px;
    flex-shrink: 0;
    opacity: 0.8;
  }
  
  .notice-content {
    flex: 1;
  }
  
  .notice-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 2px 0;
  }
  
  .notice-text {
    font-size: 10px;
    color: var(--text-secondary);
    margin: 0;
  }
</style>